import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OfferInput } from "@core/types";
import * as api from "./api";
import type { OfferFilter } from "./api";

const listKey = (filter: OfferFilter) =>
  [
    "portal",
    "offers",
    filter.active ?? null,
    filter.categoryId ?? null,
  ] as const;

export const useOffers = (filter: OfferFilter = {}) =>
  useQuery({
    queryKey: listKey(filter),
    queryFn: () => api.listOffers(filter),
  });

// SPEC-219 no pagina: la pantalla trae el catálogo completo y filtra en
// cliente (mismo criterio que `useBenefitMutations`/`useRewardMutations`),
// así que invalida toda variante de filtro de la lista.
export function useOfferMutations() {
  const client = useQueryClient();
  const invalidate = () =>
    client.invalidateQueries({ queryKey: ["portal", "offers"] });
  return {
    create: useMutation({
      mutationFn: (input: OfferInput) => api.createOffer(input),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({ id, input }: { id: string; input: OfferInput }) =>
        api.editOffer(id, input),
      onSuccess: invalidate,
    }),
    // `confirmed` viaja como query string (SPEC-219 §5/§9/§10 criterios 5 y
    // 6): la primera llamada nunca la manda; si el servidor responde 409 con
    // `OFFER_LINKED`/`OFFER_HAS_REQUESTS`, la pantalla reintenta con
    // `confirmed: true`.
    inactivate: useMutation({
      mutationFn: ({ id, confirmed }: { id: string; confirmed: boolean }) =>
        api.inactivateOffer(id, confirmed),
      onSuccess: invalidate,
    }),
  };
}
